import { api } from "./api";
import { getAll as get, getPage } from "./pagination";

export type AppraisalStatus =
  | "draft"
  | "assigned"
  | "self_assessment"
  | "self_assessment_submitted"
  | "supervisor_review"
  | "supervisor_completed"
  | "review_meeting"
  | "development_planning"
  | "final_approval"
  | "awaiting_acknowledgement"
  | "completed"
  | "returned_for_revision";

export type QuestionType =
  | "rating"
  | "yes_no"
  | "short_text"
  | "long_text"
  | "number"
  | "percentage"
  | "date"
  | "dropdown"
  | "single_choice"
  | "multi_choice"
  | "file";

export type AnsweredBy = "self" | "supervisor" | "both";
export type AnswerRole = "self" | "supervisor";

/** The question types the form builder offers, in the order they are shown.
 * Mirrors `QUESTION_TYPE_CHOICES` in the appraisal models. */
export const QUESTION_TYPE_LABELS: Record<QuestionType, string> = {
  rating: "Rating",
  yes_no: "Yes / No",
  short_text: "Short text",
  long_text: "Long text",
  number: "Number",
  percentage: "Percentage",
  date: "Date",
  dropdown: "Dropdown",
  single_choice: "Single choice",
  multi_choice: "Multiple choice",
  file: "File upload",
};

export const ANSWERED_BY_LABELS: Record<AnsweredBy, string> = {
  self: "Appraisee (self-assessment)",
  supervisor: "Supervisor",
  both: "Both, answered separately",
};

/** Types that take a list of options; the server refuses options on any other
 * type, and refuses a choice question without them. */
export const CHOICE_QUESTION_TYPES: QuestionType[] = [
  "dropdown",
  "single_choice",
  "multi_choice",
];

/** The only types that ever produce a score. */
export const SCORED_QUESTION_TYPES: QuestionType[] = [
  "rating",
  "number",
  "percentage",
];

export interface AppraisalQuestion {
  id: number;
  section: number;
  text: string;
  help_text?: string;
  description?: string;
  question_type: QuestionType;
  order: number;
  is_required: boolean;
  weight: string;
  answered_by: AnsweredBy;
  rating_scale_min?: number | null;
  rating_scale_max?: number | null;
  min_value?: string | null;
  max_value?: string | null;
  options: string[];
  requires_evidence: boolean;
  /** Slug into the evidence registry. Shows ERP figures beside the question;
   * showing them never scores it. */
  data_source?: string;
  /** The only route from an evidence figure to a score, and null by default. */
  scoring_rule?: Record<string, unknown> | null;
  conditional_on?: number | null;
  conditional_value?: string;
  is_scored: boolean;
  effective_scale: { min: number; max: number };
}

export interface AppraisalSection {
  id: number;
  template_version: number;
  title: string;
  description?: string;
  order: number;
  weight: string;
  answered_by: AnsweredBy;
  questions: AppraisalQuestion[];
}

export interface TemplateVersion {
  id: number;
  template: number;
  template_name: string;
  version_number: number;
  is_published: boolean;
  published_at?: string | null;
  has_self_assessment: boolean;
  has_review_meeting: boolean;
  has_development_planning: boolean;
  rating_scale_min: number;
  rating_scale_max: number;
  notes?: string;
  sections: AppraisalSection[];
  is_editable: boolean;
  question_count: number;
  appraisal_count: number;
}

export interface AppraisalTemplate {
  id: number;
  name: string;
  staff_category?: string;
  description?: string;
  is_active: boolean;
  created_by: number;
  created_by_name: string;
  versions: TemplateVersion[];
  current_version_id: number | null;
}

export interface AppraisalCycle {
  id: number;
  name: string;
  description?: string;
  template_version: number;
  template_name: string;
  version_number: number;
  academic_year?: number | null;
  academic_term?: number | null;
  period_start: string;
  period_end: string;
  self_assessment_due?: string | null;
  supervisor_review_due?: string | null;
  final_approval_due?: string | null;
  status: "draft" | "open" | "closed";
  appraisal_count: number;
}

export interface AppraisalAnswer {
  id: number;
  question: number;
  question_text: string;
  question_type: QuestionType;
  role: AnswerRole;
  answered_by: number;
  answered_by_name: string;
  value_text: string;
  value_number: string | null;
  value_date: string | null;
  value_choice: string[];
  score: string | null;
  overrode_suggestion: boolean;
  /** Accepted from an AI suggestion unchanged. Shown in the UI so AI-written
   * content stays identifiable in the official record. */
  ai_generated: boolean;
  /** AI-drafted, then edited by the human before saving. */
  ai_edited: boolean;
}

export interface DevelopmentGoal {
  id: number;
  appraisal: number;
  goal_type: "objective" | "training" | "development_need";
  title: string;
  description?: string;
  target_date?: string | null;
  status: string;
  carried_from?: number | null;
  ai_suggested: boolean;
  created_at: string;
}

export interface AppraisalComment {
  id: number;
  appraisal: number;
  author: number;
  author_name: string;
  stage: string;
  body: string;
  created_at: string;
}

export interface EvidenceSnapshot {
  id: number;
  appraisal: number;
  stage: AppraisalStatus;
  captured_at: string;
  captured_by_name: string;
  data: Record<string, unknown>;
}

export interface AppraisalEvidenceFile {
  id: number;
  appraisal: number;
  question?: number | null;
  file: string;
  description: string;
  uploaded_by_name: string;
  file_size?: number | null;
  created_at: string;
}

export interface AppraisalListItem {
  id: number;
  cycle: number;
  cycle_name: string;
  staff: number;
  staff_name: string;
  /** The appraisee's User id, so the client can tell whether the reader is the
   * person being appraised. */
  staff_user: number;
  staff_employee_id: string;
  department_name: string;
  supervisor: number;
  supervisor_name: string;
  approver: number | null;
  template_version: number;
  template_name: string;
  status: AppraisalStatus;
  overall_score: string | null;
  overall_band: string;
  is_overdue: boolean;
  due_date: string | null;
  created_at: string;
  updated_at: string;
}

export interface AppraisalDetail extends AppraisalListItem {
  assigned_at: string | null;
  self_submitted_at: string | null;
  supervisor_completed_at: string | null;
  review_meeting_held_on: string | null;
  review_meeting_notes: string;
  approved_at: string | null;
  acknowledged_at: string | null;
  acknowledgement_comment: string;
  returned_at: string | null;
  return_reason: string;
  returned_to_status: string;
  answers: AppraisalAnswer[];
  goals: DevelopmentGoal[];
  comments: AppraisalComment[];
  evidence_files: AppraisalEvidenceFile[];
  form: AppraisalSection[];
  /** What this user may do right now. The server decides; the UI only renders
   * what it is told, so a button is never shown to somebody who cannot use it. */
  available_actions: string[];
  stages: AppraisalStatus[];
  visible_questions: { self: number[]; supervisor: number[] };
}

export interface DashboardCounts {
  total: number;
  draft: number;
  assigned: number;
  awaiting_self_assessment: number;
  awaiting_supervisor_review: number;
  in_progress: number;
  awaiting_approval: number;
  awaiting_acknowledgement: number;
  returned: number;
  completed: number;
  overdue: number;
  overdue_by_stage: Record<string, number>;
}

export interface EvidencePayload {
  as_of: string;
  /** True once the appraisal is complete: these are the preserved figures the
   * judgements were actually made against, not today's. */
  frozen: boolean;
  period: { start: string; end: string };
  sources: Record<string, Record<string, unknown>>;
}

export interface AssignableStaff {
  id: number;
  user_id: number;
  full_name: string;
  employee_id: string;
  user_type: string;
  department: string;
  position: string;
  default_supervisor: number | null;
  default_supervisor_name: string;
}

export interface EligibleSupervisor {
  id: number;
  full_name: string;
  user_type: string;
}

export interface AnswerInput {
  question: number;
  value_text?: string;
  value_number?: number | string | null;
  value_date?: string | null;
  value_choice?: string[];
  ai_generated?: boolean;
  ai_edited?: boolean;
}

export interface AppraisalFilters {
  // Indexed so the whole object can be handed to the pagination helper, which
  // takes an open bag of query parameters.
  [key: string]: unknown;
  status?: string;
  cycle?: number | string;
  staff?: number | string;
  supervisor?: number | string;
  department?: number | string;
  template?: number | string;
  overdue?: "true";
  page?: number;
  page_size?: number;
}

export const appraisalService = {
  // --- Templates ---
  getTemplates: () => get<AppraisalTemplate>("/appraisal/templates/"),
  createTemplate: async (body: Partial<AppraisalTemplate>) =>
    (await api.post<AppraisalTemplate>("/appraisal/templates/", body)).data,
  updateTemplate: async (id: number, body: Partial<AppraisalTemplate>) =>
    (await api.patch<AppraisalTemplate>(`/appraisal/templates/${id}/`, body)).data,
  newVersion: async (id: number, fromVersion?: number) =>
    (
      await api.post<TemplateVersion>(`/appraisal/templates/${id}/new-version/`, {
        from_version: fromVersion,
      })
    ).data,

  getVersion: async (id: number) =>
    (await api.get<TemplateVersion>(`/appraisal/template-versions/${id}/`)).data,
  updateVersion: async (id: number, body: Partial<TemplateVersion>) =>
    (await api.patch<TemplateVersion>(`/appraisal/template-versions/${id}/`, body)).data,
  publishVersion: async (id: number) =>
    (await api.post<TemplateVersion>(`/appraisal/template-versions/${id}/publish/`, {}))
      .data,

  createSection: async (body: Partial<AppraisalSection>) =>
    (await api.post<AppraisalSection>("/appraisal/sections/", body)).data,
  updateSection: async (id: number, body: Partial<AppraisalSection>) =>
    (await api.patch<AppraisalSection>(`/appraisal/sections/${id}/`, body)).data,
  deleteSection: (id: number) => api.delete(`/appraisal/sections/${id}/`),

  createQuestion: async (body: Partial<AppraisalQuestion>) =>
    (await api.post<AppraisalQuestion>("/appraisal/questions/", body)).data,
  updateQuestion: async (id: number, body: Partial<AppraisalQuestion>) =>
    (await api.patch<AppraisalQuestion>(`/appraisal/questions/${id}/`, body)).data,
  deleteQuestion: (id: number) => api.delete(`/appraisal/questions/${id}/`),

  getDataSources: async () =>
    (await api.get<{ slug: string; label: string }[]>("/appraisal/data-sources/")).data,

  // --- Cycles ---
  getCycles: () => get<AppraisalCycle>("/appraisal/cycles/"),
  createCycle: async (body: Partial<AppraisalCycle>) =>
    (await api.post<AppraisalCycle>("/appraisal/cycles/", body)).data,
  updateCycle: async (id: number, body: Partial<AppraisalCycle>) =>
    (await api.patch<AppraisalCycle>(`/appraisal/cycles/${id}/`, body)).data,
  openCycle: async (id: number) =>
    (await api.post<AppraisalCycle>(`/appraisal/cycles/${id}/open/`, {})).data,
  closeCycle: async (id: number) =>
    (await api.post<AppraisalCycle>(`/appraisal/cycles/${id}/close/`, {})).data,
  assign: async (
    id: number,
    staff: number[],
    supervisors?: Record<number, number>,
  ) =>
    (
      await api.post<{
        created: AppraisalListItem[];
        created_count: number;
        skipped: { staff: number; reason: string }[];
      }>(`/appraisal/cycles/${id}/assign/`, { staff, supervisors: supervisors ?? {} })
    ).data,
  cycleProgress: async (id: number) =>
    (
      await api.get<{
        cycle: number;
        total: number;
        by_status: Record<string, number>;
        overdue: number;
      }>(`/appraisal/cycles/${id}/progress/`)
    ).data,

  getEligibleSupervisors: async () =>
    (await api.get<EligibleSupervisor[]>("/appraisal/eligible-supervisors/")).data,

  getAssignableStaff: async (cycle?: number) =>
    (
      await api.get<AssignableStaff[]>("/appraisal/assignable-staff/", {
        params: cycle ? { cycle } : undefined,
      })
    ).data,

  // --- Appraisals ---
  getAppraisals: (filters?: AppraisalFilters) =>
    getPage<AppraisalListItem>("/appraisal/appraisals/", filters),
  getAppraisal: async (id: number) =>
    (await api.get<AppraisalDetail>(`/appraisal/appraisals/${id}/`)).data,
  saveAnswers: async (id: number, answers: AnswerInput[]) =>
    (
      await api.put<AppraisalAnswer[]>(`/appraisal/appraisals/${id}/answers/`, {
        answers,
      })
    ).data,
  transition: async (
    id: number,
    action: string,
    extra?: {
      comment?: string;
      review_meeting_held_on?: string;
      review_meeting_notes?: string;
      acknowledgement_comment?: string;
    },
  ) =>
    (
      await api.post<AppraisalDetail>(`/appraisal/appraisals/${id}/transition/`, {
        action,
        ...extra,
      })
    ).data,
  reassignSupervisor: async (id: number, supervisor: number, reason: string) =>
    (
      await api.patch<AppraisalDetail>(`/appraisal/appraisals/${id}/`, {
        supervisor,
        reason,
      })
    ).data,

  getEvidence: async (id: number) =>
    (await api.get<EvidencePayload>(`/appraisal/appraisals/${id}/evidence/`)).data,
  getSnapshots: async (id: number) =>
    (await api.get<EvidenceSnapshot[]>(`/appraisal/appraisals/${id}/snapshots/`)).data,
  getSuggestedScores: async (id: number) =>
    (
      await api.get<{ suggestions: Record<string, string> }>(
        `/appraisal/appraisals/${id}/suggested-scores/`,
      )
    ).data,

  getGoals: async (id: number) =>
    (await api.get<DevelopmentGoal[]>(`/appraisal/appraisals/${id}/goals/`)).data,
  addGoal: async (id: number, body: Partial<DevelopmentGoal>) =>
    (await api.post<DevelopmentGoal>(`/appraisal/appraisals/${id}/goals/`, body)).data,

  getComments: async (id: number) =>
    (await api.get<AppraisalComment[]>(`/appraisal/appraisals/${id}/comments/`)).data,
  addComment: async (id: number, body: string) =>
    (await api.post<AppraisalComment>(`/appraisal/appraisals/${id}/comments/`, { body }))
      .data,

  uploadFile: async (id: number, form: FormData) =>
    (
      await api.post<AppraisalEvidenceFile>(
        `/appraisal/appraisals/${id}/files/`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      )
    ).data,

  // --- Dashboard and reporting ---
  getDashboard: async () =>
    (await api.get<DashboardCounts>("/appraisal/dashboard/")).data,
  getReportSummary: async () =>
    (
      await api.get<{
        total: number;
        by_department: Record<string, number>;
        by_template: Record<string, number>;
        by_status: Record<string, number>;
        completed_with_scores: number;
        average_score: number | null;
      }>("/appraisal/reports/summary/")
    ).data,
};

export default appraisalService;
